import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Header from '../components/Header';
import TariffReshoringTabs from '../components/TariffReshoringTabs';
import { api, ProductWithComponents } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import toast from 'react-hot-toast';

const ReshoringAnalysisPage = () => {
  const { productSlug } = useParams<{ productSlug: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ProductWithComponents | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProduct = async () => {
      if (!productSlug) {
        setError('Product not found');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const productData = await api.getProductBySlug(productSlug, 'all');
        setProduct(productData);
      } catch (err) {
        console.error('Failed to load product:', err);
        setError('Failed to load product data');
        toast.error('Failed to load product data');
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [productSlug]);

  // Loading state
  if (isLoading) {
    return (
      <>
        <title>Loading Reshoring Analysis - ProductTrace</title>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-gray-600">Loading reshoring analysis...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <>
        <title>Product Not Found - ProductTrace</title>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📱</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Product Not Found</h1>
              <p className="text-gray-600 mb-6">{error || 'The product you\'re looking for doesn\'t exist.'}</p>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <title>{product.name} Reshoring Analysis - ProductTrace</title>
      <meta name="description" content={`Detailed reshoring feasibility analysis for ${product.name}`} />

      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate(`/products/${productSlug}`)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to {product.name} Analysis
            </button>
          </div>

          {/* Page Header */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold text-gray-900">
                🏭 {product.name} Reshoring Analysis
              </h1>
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-green-600 font-medium">Investment Analysis</span>
              </div>
            </div>
            
            <p className="text-lg text-gray-600 mb-4">
              Alternative manufacturing feasibility analysis for {product.name} - {formatCurrency(product.retail_price)} retail
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-green-900 mb-2">Analysis Overview</h3>
              <p className="text-sm text-green-800">
                This analysis evaluates the feasibility, costs, and timeline for reshoring {product.name} manufacturing 
                to different regions, including investment requirements, ROI projections, and implementation strategies.
              </p>
            </div>
          </div>

          {/* Reshoring Analysis Component - Force reshoring tab to be active */}
          <TariffReshoringTabs 
            productSlug={productSlug!}
            productName={product.name}
            isAnalyzing={product.analysis_status === 'analyzing'}
            initialTab="reshoring"
          />
        </div>
      </div>
    </>
  );
};

export default ReshoringAnalysisPage; 